import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, ClockIcon, BabyIcon, HeartHandshakeIcon, PhoneIcon, MailIcon, ShieldCheckIcon, LanguagesIcon, AwardIcon, CalendarHeartIcon, MailCheckIcon, InfoIcon, BookOpenIcon } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
const CALENDLY_URL = 'https://calendly.com/thehopecircle-ase';
export function BookOnlinePage() {
  const {
    t,
    language
  } = useLanguage();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [{
    id: 'birthing-classes',
    title: language === 'fr' ? 'Préparation à la naissance' : 'Birthing Classes',
    description: language === 'fr' ? "Disponible en forfait complet de 7 séances ou en séances individuelles. Préparation complète à l'accouchement et à la parentalité." : 'Available as a full package of 7 sessions or as individual sessions. Complete preparation for birth and parenthood.',
    duration: '60 min',
    icon: <BookOpenIcon size={24} data-id="element-229" />
  }, {
    id: 'breastfeeding-support',
    title: language === 'fr' ? "Aide à l'allaitement" : 'Breastfeeding Support',
    description: language === 'fr' ? "Accompagnement et conseils personnalisés pour l'allaitement maternel." : 'Personalized support and guidance for breastfeeding.',
    duration: '60 min',
    icon: <HeartHandshakeIcon size={24} data-id="element-230" />
  }, {
    id: 'breastfeeding-consultation',
    title: language === 'fr' ? "Consultation d'allaitement" : 'Breastfeeding Consultation',
    description: language === 'fr' ? 'Consultation approfondie en allaitement avec évaluation complète et plan de soins personnalisé.' : 'In-depth breastfeeding consultation with full assessment and personalized care plan.',
    duration: '60 min',
    icon: <BabyIcon size={24} data-id="element-231" />,
    comingSoon: true
  }];
  return <div className="pt-24 pb-20 bg-white min-h-screen" data-id="element-232">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-teal-50/50" data-id="element-233">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-234">
          <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-teal-100/40 blur-3xl" data-id="element-235" />
          <div className="absolute top-[40%] -left-[10%] w-[50%] h-[50%] rounded-full bg-teal-50/60 blur-3xl" data-id="element-236" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-237">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-238">
            <h1 className="text-4xl md:text-5xl font-serif font-medium text-teal-900 mb-6" data-id="element-239">
              Book Your Online Consultation
            </h1>
            <p className="text-xl text-slate-600 mb-4" data-id="element-240">
              Expert midwifery support from the comfort of your home
            </p>
            <p className="text-sm font-medium text-teal-600 tracking-wide uppercase mb-10" data-id="element-241">
              → Available across New Zealand & internationally
            </p>

            <button onClick={() => document.getElementById('booking-calendly')?.scrollIntoView({
            behavior: 'smooth'
          })} className="inline-flex justify-center items-center bg-teal-600 hover:bg-teal-700 text-white px-8 py-3.5 rounded-full text-base font-medium transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5" data-id="element-242">
              Book Now
            </button>
          </motion.div>
        </div>
      </section>

      {/* Clarity Notice */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10" data-id="element-243">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-white rounded-2xl shadow-lg border border-teal-100 p-6 md:p-8 text-center" data-id="element-244">
          <p className="text-lg font-medium text-slate-800 mb-2" data-id="element-245">
            Online consultations can be booked instantly below.
          </p>
          <p className="text-slate-600" data-id="element-246">
            For in-person services in Auckland, please contact me directly via
            email or text.
          </p>
        </motion.div>
      </section>

      {/* Services Selection */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-247">
        <div className="text-center mb-12" data-id="element-248">
          <h2 className="text-3xl font-serif font-medium text-teal-900 mb-4" data-id="element-249">
            {language === 'fr' ? 'Choisir un service' : 'Select a Service'}
          </h2>
          <p className="text-slate-600" data-id="element-250">
            {language === 'fr' ? 'Choisissez la consultation qui correspond le mieux à vos besoins' : 'Choose the consultation that best fits your needs'}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-251">
          {services.map((service, index) => <motion.div key={service.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-2xl p-8 border-2 border-teal-50 hover:border-teal-200 hover:shadow-md transition-all" data-id="element-252">
              {service.comingSoon && <div className="mb-4" data-id="element-253">
                  <span className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full" data-id="element-254">
                    {language === 'fr' ? 'Bientôt disponible (Septembre)' : 'Coming Soon (September)'}
                  </span>
                </div>}
              <div className="w-14 h-14 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center mb-6" data-id="element-255">
                {service.icon}
              </div>
              <h3 className="text-xl font-medium text-slate-800 mb-3" data-id="element-256">
                {service.title}
              </h3>
              <p className="text-slate-600 mb-6 min-h-[80px]" data-id="element-257">
                {service.description}
              </p>
              <div className="flex items-center text-sm font-medium text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg w-fit" data-id="element-258">
                <ClockIcon size={16} className="mr-2" data-id="element-259" />
                {service.duration}
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Calendly Booking Section */}
      <section id="booking-calendly" className="py-16 bg-teal-50/30" data-id="element-260">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-261">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-10" data-id="element-262">
            <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-263">
              <CalendarIcon size={28} data-id="element-264" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-teal-900 mb-4" data-id="element-265">
              Book Your Appointment
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed" data-id="element-266">
              Choose a time that works best for you. I look forward to
              supporting you on your journey — you're in safe, caring hands.
            </p>
          </motion.div>

          {/* Calendly Embed */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.15
        }} className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-267">
            <iframe src={`${CALENDLY_URL}?hide_gdpr_banner=1&background_color=ffffff&text_color=334155&primary_color=0d9488`} width="100%" height="700" frameBorder="0" title="Schedule an appointment" className="w-full min-h-[700px]" data-id="element-268" />
          </motion.div>

          {/* After Booking Note */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="mt-8 bg-white rounded-2xl border border-teal-100/60 p-6 md:p-8" data-id="element-269">
            <h3 className="text-lg font-medium text-teal-900 mb-4 text-center" data-id="element-270">
              What happens next?
            </h3>
            <div className="grid sm:grid-cols-3 gap-6" data-id="element-271">
              <div className="flex flex-col items-center text-center" data-id="element-272">
                <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mb-3" data-id="element-273">
                  <MailCheckIcon size={20} data-id="element-274" />
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1" data-id="element-275">
                  Confirmation Email
                </p>
                <p className="text-sm text-slate-500" data-id="element-276">
                  You'll receive an email with all the details of your
                  appointment.
                </p>
              </div>
              <div className="flex flex-col items-center text-center" data-id="element-277">
                <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mb-3" data-id="element-278">
                  <CalendarHeartIcon size={20} data-id="element-279" />
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1" data-id="element-280">
                  Calendar Invite
                </p>
                <p className="text-sm text-slate-500" data-id="element-281">
                  A calendar event will be added so you never miss your session.
                </p>
              </div>
              <div className="flex flex-col items-center text-center" data-id="element-282">
                <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mb-3" data-id="element-283">
                  <InfoIcon size={20} data-id="element-284" />
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1" data-id="element-285">
                  Easy Rescheduling
                </p>
                <p className="text-sm text-slate-500" data-id="element-286">
                  Need to change your time? You can reschedule directly from the
                  email.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-16 border-t border-slate-100" data-id="element-287">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-288">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-289">
            <div className="flex flex-col items-center text-center" data-id="element-290">
              <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mb-4" data-id="element-291">
                <ShieldCheckIcon size={24} data-id="element-292" />
              </div>
              <h4 className="font-medium text-slate-800 mb-1" data-id="element-293">
                Certified Midwife
              </h4>
              <p className="text-sm text-slate-500" data-id="element-294">16+ years experience</p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-295">
              <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mb-4" data-id="element-296">
                <LanguagesIcon size={24} data-id="element-297" />
              </div>
              <h4 className="font-medium text-slate-800 mb-1" data-id="element-298">Bilingual</h4>
              <p className="text-sm text-slate-500" data-id="element-299">English & French</p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-300">
              <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mb-4" data-id="element-301">
                <HeartHandshakeIcon size={24} data-id="element-302" />
              </div>
              <h4 className="font-medium text-slate-800 mb-1" data-id="element-303">
                Trauma-Informed
              </h4>
              <p className="text-sm text-slate-500" data-id="element-304">Compassionate care</p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-305">
              <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mb-4" data-id="element-306">
                <AwardIcon size={24} data-id="element-307" />
              </div>
              <h4 className="font-medium text-slate-800 mb-1" data-id="element-308">BBC 100 Women</h4>
              <p className="text-sm text-slate-500" data-id="element-309">Honoree</p>
            </div>
          </div>
        </div>
      </section>

      {/* In-Person Contact Section */}
      <section className="py-16 bg-teal-900 text-white" data-id="element-310">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-311">
          <h2 className="text-2xl md:text-3xl font-serif font-medium mb-6 text-[#F0FDFA]" data-id="element-312">
            In-Person Consultations (Auckland Only)
          </h2>
          <p className="text-teal-100 mb-10 max-w-2xl mx-auto text-lg" data-id="element-313">
            For home visits or in-person support in the Auckland area, please
            get in touch directly to discuss availability.
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-10" data-id="element-314">
            <div className="flex items-center space-x-3 bg-teal-800/50 px-6 py-4 rounded-2xl" data-id="element-315">
              <MailIcon className="text-teal-300" size={24} data-id="element-316" />
              <div className="text-left" data-id="element-317">
                <p className="text-xs text-teal-200 uppercase tracking-wider" data-id="element-318">
                  Email
                </p>
                <a href="mailto:thehopecircle.ase@gmail.com" className="text-white hover:text-teal-200 transition-colors" data-id="element-319">
                  thehopecircle.ase@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-center space-x-3 bg-teal-800/50 px-6 py-4 rounded-2xl" data-id="element-320">
              <PhoneIcon className="text-teal-300" size={24} data-id="element-321" />
              <div className="text-left" data-id="element-322">
                <p className="text-xs text-teal-200 uppercase tracking-wider" data-id="element-323">
                  Phone
                </p>
                <p className="text-white" data-id="element-324">+64(0)273737738</p>
              </div>
            </div>
          </div>

          <a href="mailto:thehopecircle.ase@gmail.com" className="inline-flex justify-center items-center bg-white text-teal-900 hover:bg-teal-50 px-8 py-3.5 rounded-full text-base font-medium transition-colors shadow-sm" data-id="element-325">
            Contact Me
          </a>
        </div>
      </section>
    </div>;
}